import { useTranslation } from "react-i18next";
import { Link } from "react-router";
import type { ItemChange, Review } from "../../../api/content/reviewDecoders";
import { appPaths } from "../../../shared/routing/appPaths";
import { FIELD_DIFF_KINDS } from "../diff/definitionKinds";
import LabelListUnit from "../labels/LabelListUnit";
import QuestionFold from "../questions/QuestionFold";
import { changedTextFields } from "../diff/textFields";
import DefinitionDiff from "../page/DefinitionDiff";
import TextFieldDiffs from "../page/TextFieldDiffs";
import TripleList from "../page/TripleList";
import Chip from "../../../ui/feedback/Chip";
import { itemKindText } from "../../workflows/inputCards/typeWords";
import { useReviewTermNames } from "../conflicts/conflictTermNames";

/** What the review page lets a unit change: the change set's content where `canEdit`, and the reload after a write. */
export interface ItemEdits {
  readonly canEdit: boolean;
  readonly onChanged: () => void;
}

/** A shown-only unit writes nothing, so nothing reloads. */
const NO_CHANGE = () => undefined;

/**
 * A changed item that is not split into terms (CU-3): its triples in plain language for
 * shapes, a module's statements about no named term, and examples; the kind's own diff of
 * fields for questions, prompts, steps, Workflows, tools and skills; a label list as its
 * labels and the decisions it needs (CU-4); a question with its fold (D6). Without `edits` it
 * only shows, as where a step of the Run shows the change set: no fold, no label decision.
 */
export default function ItemUnit({ item, review, edits }: { item: ItemChange; review: Review; edits: ItemEdits | null }) {
  const { t } = useTranslation();
  const canEdit = edits?.canEdit ?? false;
  const onChanged = edits?.onChanged ?? NO_CHANGE;
  const labels = useReviewTermNames(review);
  const textFields = changedTextFields(item);
  return (
    <div className="space-y-3">
      <p className="text-base">
        <Chip className="mr-2">{itemKindText(item.kind, t)}</Chip>
        {t(`reviews.curation.itemSentence.${item.change}`, { name: item.name })}
      </p>
      {item.triples !== null ? (
        <>
          <TripleList title={t("reviews.item.added", { count: item.triples.addedCount })} lines={item.triples.added} labels={labels} tone="added" />
          <TripleList title={t("reviews.item.removed", { count: item.triples.removedCount })} lines={item.triples.removed} labels={labels} tone="removed" />
        </>
      ) : (
        <>
          <p className="text-sm">{t("reviews.item.changedFields", { fields: (item.changedFields ?? []).join(", ") || "—" })}</p>
          {textFields.length > 0 ? <TextFieldDiffs item={item} fields={textFields} /> : null}
          {item.kind === "label_list" ? <LabelListUnit item={item} review={review} canEdit={canEdit} onChanged={onChanged} /> : null}
          {FIELD_DIFF_KINDS.has(item.kind) && item.kind !== "label_list" ? <DefinitionDiff item={item} /> : null}
        </>
      )}
      {item.kind === "competency_question" && edits !== null ? <QuestionFold item={item} review={review} onChanged={onChanged} /> : null}
      <p className="flex flex-wrap items-center gap-3 text-xs">
        {item.newReference !== null ? (
          <Link to={appPaths.item(item.newReference)} className="app-link underline">
            {t("reviews.item.openNew")}
          </Link>
        ) : null}
        {item.oldReference !== null ? (
          <Link to={appPaths.item(item.oldReference)} className="app-link underline">
            {t("reviews.item.openOld")}
          </Link>
        ) : null}
      </p>
    </div>
  );
}
